import { useState, useEffect, useRef, useCallback } from 'react';
import type { Message } from '../../types/chat';
import { ChatMessage } from './ChatMessage';

export interface TeamSearchResult {
  team_id: string;
  name: string;
  abbreviation: string | null;
  sport: string;
  region: string | null;
  competition_id: string;
  competition_name: string;
  competition_period_id: string;
  season_period_id: string;
}

interface MessageListProps {
  messages: Message[];
  sessionId: string;
}

export function MessageList({ messages, sessionId }: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  const checkIfAtBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return true;
    const threshold = 50;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  const handleScroll = useCallback(() => {
    isAtBottomRef.current = checkIfAtBottom();
  }, [checkIfAtBottom]);

  useEffect(() => {
    if (isAtBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto px-6 py-6 space-y-6 h-full"
    >
      {messages.map((message, index) => {
        const userPrompt =
          message.role === 'assistant' && index > 0 && messages[index - 1].role === 'user'
            ? messages[index - 1].content
            : undefined;
        return (
          <ChatMessage
            key={message.id}
            message={message}
            userPrompt={userPrompt}
            sessionId={sessionId}
          />
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}

// Hudl logo SVG path (shared between screens)
const HUDL_LOGO_PATH = "M357.281 301.023C356.906 301.023 356.541 300.873 356.272 300.593C355.878 300.204 355.734 299.615 355.898 299.066C364.118 271.442 366.827 242.88 363.974 214.188C363.677 213.749 363.437 213.399 363.129 212.97C335.845 199.787 318.164 171.115 318.097 139.928C318.068 129.279 320.008 118.93 323.859 109.17C323.792 108.631 323.744 108.212 323.677 107.673C319.144 101.495 314.121 95.3677 308.753 89.4796C279.134 56.9553 239.365 34.5106 196.772 26.2872C196.1 26.1475 195.61 25.5587 195.581 24.8501C195.562 24.1515 196.023 23.5128 196.676 23.3332C213.31 18.9121 230.232 16.6666 246.99 16.6666C293.012 16.6666 336.459 32.345 372.637 62.0151C372.896 62.2246 373.213 62.3344 373.54 62.3344C373.684 62.3344 373.828 62.3145 373.962 62.2646C380.743 60.1089 388.234 58.9513 395.657 58.9313C438.519 58.9313 473.487 95.0883 473.602 139.529C473.65 161.734 464.69 183.24 449.026 198.539C448.872 199.008 448.738 199.388 448.584 199.847C449.813 209.348 450.437 218.848 450.437 228.09C450.437 228.968 450.38 229.836 450.332 230.704L450.226 232.351C450.217 232.7 450.101 233.02 449.909 233.259C425.813 264.347 393.948 287.739 357.761 300.933L357.281 301.023ZM102.664 376.241C102.27 376.241 101.896 376.081 101.627 375.792C64.1625 336.062 43.5239 283.608 43.5239 228.09C43.5239 218.809 44.1577 209.308 45.3966 199.857C45.2334 199.388 45.1085 199.009 44.9453 198.55C14.6933 168.999 9.43999 116.296 45.8192 80.1588C53.6847 72.3346 63.2117 66.227 73.603 62.8338C89.0363 57.804 105.161 57.6842 120.037 62.225C120.181 62.265 120.316 62.2849 120.45 62.2849C120.777 62.2849 121.094 62.1752 121.353 61.9556C130.237 54.6404 139.591 48.1634 149.137 42.7144C149.319 42.6745 149.492 42.6446 149.684 42.6146C187.917 48.8121 223.394 65.8477 252.484 91.9051C252.906 92.2744 253.079 92.8532 252.944 93.4021C252.81 93.961 252.387 94.3901 251.85 94.5198C224.287 101.017 198.789 113.092 176.056 130.387C175.643 130.697 175.413 131.216 175.461 131.755C175.749 134.659 175.884 137.333 175.874 139.928C175.778 179.848 147.024 214.118 108.993 219.657C108.58 219.987 108.244 220.246 107.831 220.575C105.104 227.531 102.674 234.717 100.619 241.942C88.3641 284.846 89.5741 331.821 104.037 374.225C104.258 374.904 104.009 375.632 103.432 376.012C103.202 376.161 102.933 376.241 102.664 376.241ZM247.749 487.038C219.927 487.038 194.044 471.479 180.225 446.41C176.565 439.803 173.828 432.638 172.08 425.123C171.773 424.754 171.523 424.444 171.216 424.075C160.873 419.744 150.923 414.574 141.646 408.716C141.444 408.466 141.252 408.227 141.051 407.977C127.26 370.782 123.725 330.464 130.813 291.363C130.919 290.804 131.312 290.355 131.841 290.195C131.937 290.185 132.033 290.185 132.129 290.185C132.743 290.185 133.06 290.355 133.3 290.614C152.489 312.101 175.259 328.967 200.969 340.743C201.151 340.823 201.353 340.863 201.555 340.863C201.852 340.863 202.15 340.773 202.4 340.583C204.685 338.877 206.856 337.42 209.007 336.132C220.753 329.137 234.073 325.444 247.528 325.444C271.307 325.444 294.231 337.041 308.848 356.481C309.127 356.851 309.549 357.06 309.991 357.06C310.058 357.06 310.126 357.05 310.193 357.04C317.271 356.042 324.474 354.635 331.6 352.859C373.54 342.44 412.128 317.819 440.257 283.549C440.526 283.209 440.939 283.02 441.362 283.02C441.602 283.02 441.832 283.08 442.053 283.199C442.649 283.539 442.946 284.247 442.764 284.936C426.034 347.26 381.684 399.145 324.118 423.746C323.811 424.125 323.561 424.424 323.254 424.803C318.318 446.52 304.863 465.302 286.356 476.329C274.582 483.335 261.233 487.038 247.749 487.038Z";

export function TeamSelectionScreen({
  onTeamSelect,
  onSearchTeams,
}: {
  onTeamSelect: (team: TeamSearchResult) => void;
  onSearchTeams: (query: string) => Promise<TeamSearchResult[]>;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TeamSearchResult[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed || isSearching) return;

    setIsSearching(true);
    setError(null);
    setResults(null);

    try {
      const teams = await onSearchTeams(trimmed);
      setResults(teams);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="flex-1 flex items-start justify-center h-full p-8 overflow-y-auto">
      <div className="text-center max-w-lg w-full">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
          <svg className="w-9 h-9" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d={HUDL_LOGO_PATH} fill="white"/>
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Which basketball team would you like to analyze?</h2>
        <p className="text-gray-500 mb-8">
          Search for a team to get started with game analysis, stats breakdowns, and performance insights.
        </p>
        <div className="flex gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g., Kansas, Duke..."
            className="flex-1 px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            autoFocus
            disabled={isSearching}
          />
          <button
            onClick={handleSearch}
            disabled={!query.trim() || isSearching}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold shadow-md hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {isSearching && (
          <div className="mt-8 flex justify-center">
            <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="mt-6 text-red-600 text-sm">
            {error}
          </div>
        )}

        {results !== null && !isSearching && (
          <div className="mt-6">
            {results.length === 0 ? (
              <p className="text-gray-500">No teams found. Try a different search term.</p>
            ) : (
              <div className="space-y-3 text-left max-h-[50vh] overflow-y-auto">
                {results.map((team, idx) => (
                  <button
                    key={`${team.team_id}-${team.competition_id}-${idx}`}
                    onClick={() => onTeamSelect(team)}
                    className="w-full px-5 py-4 bg-white rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50 cursor-pointer transition-all text-left"
                  >
                    <div className="font-semibold text-gray-900">{team.name}</div>
                    <div className="text-sm text-gray-500 mt-0.5">{team.competition_name}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function TeamWelcomeScreen({ team }: { team: TeamSearchResult }) {
  return (
    <div className="flex-1 flex items-center justify-center h-full p-8">
      <div className="text-center max-w-lg w-full">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
          <svg className="w-9 h-9" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d={HUDL_LOGO_PATH} fill="white"/>
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">{team.name}</h2>
        <p className="text-gray-500 mb-8">{team.competition_name}</p>

        <p className="text-gray-700 mb-6">
          Ask me anything about this team. Here are some things I can help with:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left text-sm">
          <div className="px-4 py-3 bg-white rounded-xl border border-gray-200">
            <div className="font-semibold text-gray-900 mb-1">Game Analysis</div>
            <div className="text-gray-500">Break down any game with stats, runs, and key moments</div>
          </div>
          <div className="px-4 py-3 bg-white rounded-xl border border-gray-200">
            <div className="font-semibold text-gray-900 mb-1">Stats & Efficiency</div>
            <div className="text-gray-500">Four Factors, shooting splits, player comparisons</div>
          </div>
          <div className="px-4 py-3 bg-white rounded-xl border border-gray-200">
            <div className="font-semibold text-gray-900 mb-1">Scouting Reports</div>
            <div className="text-gray-500">Scout upcoming opponents and identify tendencies</div>
          </div>
          <div className="px-4 py-3 bg-white rounded-xl border border-gray-200">
            <div className="font-semibold text-gray-900 mb-1">Video Breakdown</div>
            <div className="text-gray-500">Analyze specific plays, spacing, and technique</div>
          </div>
        </div>
        <p className="text-gray-400 text-sm mt-6">Type a question below to get started</p>
      </div>
    </div>
  );
}
